// @flow
import React, { useEffect, useState, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Box, Flex } from '@rebass/grid';
import { Checkbox, Dropdown, Input } from 'freemium-ui';
import Icon from 'components/Icons';
import { useComponentGroups } from 'shared/hooks';
import isEqual from 'lodash.isequal';
import { Trans, useTranslation } from 'react-i18next';

const items = [
  {
    id: 'OP',
    icon: 'Operational',
    label: 'Operational'
  },
  {
    id: 'PD',
    icon: 'Degraded',
    label: 'Degraded Performance'
  },
  {
    id: 'UM',
    icon: 'Maintenance',
    label: 'Under Maintenance'
  },
  {
    id: 'PO',
    icon: 'Partial',
    label: 'Partial Outage'
  },
  {
    id: 'MO',
    icon: 'Major',
    label: 'Major Outage'
  }
];
const customRender = item => (
  <CustomSelect>
    <span>
      <Icon
        size={13}
        viewBox="0 0 12 12"
        glyph={item.icon && item.icon.toLowerCase()}
      />
    </span>
    <span>{item.label}</span>
  </CustomSelect>
);

type ComponentTreeProps = {
  initialSelectedComponents: [{ component: any, new_status: string }],
  actionCallback: any,
  components: [any],
  statusOptions: any,
  title: string,
  overrideComponentStatus: boolean,
  isPlanned: boolean,
  compact?: boolean,
  isDrawer: boolean
};

const ComponentsTree = ({
  initialSelectedComponents,
  actionCallback,
  components,
  statusOptions,
  title,
  compact = false,
  overrideComponentStatus,
  isDrawer,
  isPlanned
}: ComponentTreeProps) => {
  const { t } = useTranslation();
  const { data: componentGroups = [] } = useComponentGroups();
  const [searchText, setSearchText] = React.useState('');
  const [componentEntities, setComponentEntities] = useState();
  const [selectedStatus, setSelectedStatus] = useState();
  const [selectAll, setSelectAll] = useState(false);
  const componentsRef = useRef();
  const selectedComponentsRef = useRef();
  const isModified = useRef(false);
  const sortedComponents = useMemo(
    () =>
      components.sort(
        (a, b) => a.component_mini.order - b.component_mini.order
      ),
    [components]
  );
  const [componentIdsWithGroup, componentIdsWithoutGroup] = useMemo(() => {
    const filteredComponents = (() => {
      if (!searchText || !components || components.length === 0)
        return sortedComponents;
      return sortedComponents.filter(
        c =>
          c.component_mini.name
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (c.component_mini.group_name &&
            c.component_mini.group_name
              .toLowerCase()
              .includes(searchText.toLowerCase()))
      );
    })();
    const groupComponents = {};
    let noGroupComponents = filteredComponents.filter(eachComponent => {
      const parentGroupId = eachComponent.component_mini.group;
      const parentGroup =
        componentGroups &&
        componentGroups.find(eachGroup => parentGroupId === eachGroup.id);
      if (!parentGroupId) return true;
      if (groupComponents[parentGroupId]) {
        groupComponents[parentGroupId].push(eachComponent.component);
      } else {
        groupComponents[parentGroupId] = [eachComponent.component];
      }
      if (
        parentGroup &&
        parentGroup.parent &&
        !groupComponents[parentGroup.parent]
      ) {
        groupComponents[parentGroup.parent] = [];
      }
      return false;
    });
    noGroupComponents = noGroupComponents.map(
      eachComponent => eachComponent.component
    );
    return [groupComponents, noGroupComponents];
  }, [searchText, sortedComponents, componentGroups]);

  const selectedComponentsCount = useMemo(() => {
    let count = 0;
    if (componentEntities) {
      Object.keys(componentEntities).forEach(eachComponent => {
        if (componentEntities[eachComponent].selected) count += 1;
      });
    }
    return count;
  }, [componentEntities, selectAll]);

  useEffect(() => {
    // to handle template creation where the user could select between inc and sm and hence statusOption changes.
    if (
      statusOptions &&
      statusOptions.length !== undefined &&
      componentEntities
    ) {
      const statusOptionsObj = statusOptions.reduce((acc, option) => {
        acc[option.id] = true;
        return acc;
      }, {});
      const temp = { ...componentEntities };
      let flag = false;
      Object.keys(temp).forEach(eachcompKey => {
        if (!statusOptionsObj[temp[eachcompKey].status]) {
          temp[eachcompKey].status = statusOptions[0].id;
          flag = true;
        }
      });
      if (flag) {
        setComponentEntities(temp);
      }
    }
  }, [statusOptions]);

  useEffect(() => {
    if (
      !isEqual(initialSelectedComponents, selectedComponentsRef.current) ||
      !isEqual(components, componentsRef.current)
    ) {
      const temp = {};
      sortedComponents.forEach(eachEntity => {
        temp[eachEntity.component] = {
          ...eachEntity,
          selected: false
        };
      });
      if (initialSelectedComponents) {
        initialSelectedComponents.forEach(eachSelectedComponent => {
          if (temp[eachSelectedComponent.component]) {
            temp[eachSelectedComponent.component].selected = true;
            if (overrideComponentStatus) {
              temp[eachSelectedComponent.component].status =
                eachSelectedComponent.new_status;
            }
          }
        });
        selectedComponentsRef.current = initialSelectedComponents.slice();
      }
      setComponentEntities(temp);
      componentsRef.current = components.slice();
    }
  }, [initialSelectedComponents, sortedComponents]);

  useEffect(() => {
    if (componentEntities) {
      actionCallback(componentEntities, isModified.current);
    }
  }, [componentEntities]);

  const modifySelectedComponents = component => {
    isModified.current = true;
    const comp = componentEntities[component.component];
    setComponentEntities({
      ...componentEntities,
      [component.component]: {
        ...comp,
        selected: !comp.selected,
        status: isPlanned
          ? 'UM'
          : comp.selected
          ? comp.status
          : statusOptions.find(option => option.id === comp.status)
          ? comp.status
          : components.find(a => a.component === Number(comp.component))
              .status || statusOptions[0].id
      }
    });
  };

  const modifyComponentStatus = (component, newStatus) => {
    isModified.current = true;
    setComponentEntities({
      ...componentEntities,
      [component.component]: {
        ...componentEntities[component.component],
        status: newStatus.id
      }
    });
  };

  // override all selected component status change
  const modifyAllComponentStatus = updatedStatus => {
    setSelectedStatus(updatedStatus);
    const updated = { ...componentEntities };
    if (componentEntities) {
      Object.keys(updated).forEach(eachComponent => {
        if (componentEntities[eachComponent].selected) {
          componentEntities[eachComponent].status = updatedStatus.id;
          updated[eachComponent].status = updatedStatus.id;
        }
        return eachComponent;
      });
      isModified.current = true;
      setComponentEntities(updated);
    }
  };

  const toggleComps = (group, subGroups, checked) => {
    const temp = { ...componentEntities };
    componentIdsWithGroup[group] &&
      componentIdsWithGroup[group].forEach(eachComponent => {
        temp[eachComponent].selected = checked;
        temp[eachComponent].status = temp[eachComponent.selected]
          ? temp[eachComponent].status
          : statusOptions.find(
              option => option.id === temp[eachComponent].status
            )
          ? temp[eachComponent].status
          : statusOptions[0].id;
      });
    setComponentEntities(temp);
  };

  const selectComponentsByGroup = (group, checked) => {
    isModified.current = true;
    const subGroups = componentGroups.filter(d => d.parent === group);
    if (subGroups.length) {
      subGroups.forEach(sg => {
        toggleComps(sg.id, [], checked);
      });
    }
    toggleComps(group, subGroups, checked);
  };

  const getSubgroup = id => componentGroups.filter(g => g.parent === id);

  const checkedStatus = eachGroup => {
    const subGroups = componentGroups.filter(
      each => each.parent === eachGroup.id
    );
    let subGroupComp = true;
    if (subGroups.length)
      subGroupComp = subGroups.find(
        sg =>
          componentIdsWithGroup[sg.id] &&
          !componentIdsWithGroup[sg.id].find(
            eachComponentId =>
              componentEntities && !componentEntities[eachComponentId].selected
          )
      );
    const common =
      componentIdsWithGroup[eachGroup.id] &&
      !componentIdsWithGroup[eachGroup.id].find(
        eachComponentId =>
          componentEntities && !componentEntities[eachComponentId].selected
      );
    return common && subGroupComp;
  };

  // select all checkbox change
  const selectAllFiltered = checked => {
    setSelectAll(checked);
    if (componentEntities)
      Object.keys(componentEntities).forEach(c => {
        if (
          componentEntities[c].component_mini.name
            .toLowerCase()
            .includes(searchText.toLowerCase())
        ) {
          if (!checked)
            componentEntities[c].status = components.find(
              a => a.component === Number(c)
            ).status;
          componentEntities[c].selected = checked;
        }
      });
  };

  return (
    <>
      {componentGroups && componentEntities && (
        <TreeWrapper isDrawer={isDrawer}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            pb={2}
            mb={3}
            className="title-container"
          >
            <div
              className="title"
              style={{
                ...(compact ? { color: '#12344D' } : {}),
                fontSize: compact ? '18px' : '12px',
                fontWeight: compact ? 600 : 500
              }}
            >
              {title}{' '}
              {!compact && `(${selectedComponentsCount}/${components.length})`}
            </div>
            {!isPlanned && selectedComponentsCount > 1 ? (
              <Flex alignItems="center">
                {!compact && (
                  <Box
                    fontSize="12px"
                    mr="8px"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Change all selected to
                  </Box>
                )}
                <Dropdown
                  width="200px"
                  defaultSelectedItem={
                    (selectedStatus &&
                      statusOptions.find(
                        status => status.id === selectedStatus.id
                      )) ||
                    null
                  }
                  items={statusOptions || []}
                  itemToString={customRender}
                  itemComponent={customRender}
                  onChange={newStatus => {
                    modifyAllComponentStatus(newStatus);
                  }}
                />
              </Flex>
            ) : null}
          </Flex>
          <StyledSearchInput>
            <Input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder={t(
                'components.componentsTree.searchServicesPlaceholder'
              )}
              startAdornment={
                <IconHolder>
                  <Icon glyph="search" size="17" viewBox="-4 -3 20 20" />
                </IconHolder>
              }
            />
          </StyledSearchInput>
          <Box pl={1} pr={1}>
            {Object.keys(componentIdsWithGroup).length === 0 &&
              componentIdsWithoutGroup.length === 0 && (
                <BlankState>
                  <p>
                    <Trans i18nKey="components.componentsTree.emptyMessage">
                      <br />
                    </Trans>
                  </p>
                </BlankState>
              )}
            {searchText.length && Object.keys(componentIdsWithGroup).length ? (
              <Box pb="3">
                <Checkbox
                  onChange={(e, checked) => selectAllFiltered(checked)}
                  checked={selectAll}
                >
                  <b>{t('components.componentsTree.selectAll')}</b>
                </Checkbox>
              </Box>
            ) : null}
            {componentGroups
              .filter(d => !d.parent)
              .map((eachGroup, index) => (
                <React.Fragment key={`ct-group-${index}`}>
                  {componentIdsWithGroup[eachGroup.id] ? (
                    <>
                      <Box pb="2">
                        <Checkbox
                          onChange={(e, checked) => {
                            selectComponentsByGroup(eachGroup.id, checked);
                          }}
                          checked={checkedStatus(eachGroup)}
                        >
                          {eachGroup.name}
                        </Checkbox>
                      </Box>
                      <Box pl="4" pb="2">
                        {componentIdsWithGroup[eachGroup.id].map(
                          (componentInGroup, cgIndex) => {
                            const eachComponent =
                              componentEntities[componentInGroup];
                            return (
                              <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                mb="2"
                                key={`ct-groupcomponent-${cgIndex}`}
                              >
                                <Box width="55%" mr={1}>
                                  <Checkbox
                                    onChange={() => {
                                      modifySelectedComponents(eachComponent);
                                    }}
                                    checked={eachComponent.selected}
                                  >
                                    {eachComponent.component_mini.name}
                                  </Checkbox>
                                </Box>
                                {eachComponent.selected ? (
                                  <Dropdown
                                    width="200px"
                                    defaultSelectedItem={
                                      statusOptions.find(
                                        status =>
                                          status.id === eachComponent.status
                                      ) || statusOptions[0]
                                    }
                                    items={statusOptions || []}
                                    itemToString={customRender}
                                    itemComponent={customRender}
                                    disabled={!eachComponent.selected}
                                    onChange={newStatus => {
                                      modifyComponentStatus(
                                        eachComponent,
                                        newStatus
                                      );
                                    }}
                                  />
                                ) : (
                                  <StatusContainer>
                                    {customRender(
                                      items.find(
                                        item => item.id === eachComponent.status
                                      )
                                    )}
                                  </StatusContainer>
                                )}
                              </Flex>
                            );
                          }
                        )}
                      </Box>
                      <Box pl="4">
                        {getSubgroup(eachGroup.id).map((sb, idx) => (
                          <React.Fragment key={`${sb.name}-${idx}`}>
                            {componentIdsWithGroup[sb.id] ? (
                              <>
                                <Box pb="2">
                                  <Checkbox
                                    onChange={(e, checked) => {
                                      selectComponentsByGroup(sb.id, checked);
                                    }}
                                    checked={checkedStatus(sb)}
                                  >
                                    {sb.name}
                                  </Checkbox>
                                </Box>
                                <Box pl="4" pb="2">
                                  {componentIdsWithGroup[sb.id].map(
                                    (componentInGroup, cgIndex) => {
                                      const eachComponent =
                                        componentEntities[componentInGroup];
                                      return (
                                        <Flex
                                          alignItems="center"
                                          justifyContent="space-between"
                                          mb="2"
                                          key={`ct-groupcomponent-${cgIndex}`}
                                        >
                                          <Box width="50%" mr={1}>
                                            <Checkbox
                                              onChange={() => {
                                                modifySelectedComponents(
                                                  eachComponent
                                                );
                                              }}
                                              checked={eachComponent.selected}
                                            >
                                              {
                                                eachComponent.component_mini
                                                  .name
                                              }
                                            </Checkbox>
                                          </Box>
                                          {eachComponent.selected ? (
                                            <Dropdown
                                              width="200px"
                                              defaultSelectedItem={
                                                statusOptions.find(
                                                  status =>
                                                    status.id ===
                                                    eachComponent.status
                                                ) || statusOptions[0]
                                              }
                                              items={statusOptions || []}
                                              itemToString={customRender}
                                              itemComponent={customRender}
                                              disabled={!eachComponent.selected}
                                              onChange={newStatus => {
                                                modifyComponentStatus(
                                                  eachComponent,
                                                  newStatus
                                                );
                                              }}
                                            />
                                          ) : (
                                            <StatusContainer>
                                              {customRender(
                                                items.find(
                                                  item =>
                                                    item.id ===
                                                    eachComponent.status
                                                )
                                              )}
                                            </StatusContainer>
                                          )}
                                        </Flex>
                                      );
                                    }
                                  )}
                                </Box>
                              </>
                            ) : null}
                          </React.Fragment>
                        ))}
                      </Box>
                    </>
                  ) : null}
                </React.Fragment>
              ))}
            {componentIdsWithoutGroup.map((componentNotInGroup, cwgIndex) => {
              const eachComponent = componentEntities[componentNotInGroup];
              return (
                <Flex
                  alignItems="center"
                  key={`ct-groupwogroup-${cwgIndex}`}
                  justifyContent="space-between"
                  mb="2"
                >
                  <Box width="60%">
                    <Checkbox
                      onChange={() => {
                        modifySelectedComponents(eachComponent);
                      }}
                      checked={eachComponent.selected}
                    >
                      {eachComponent.component_mini.name}
                    </Checkbox>
                  </Box>
                  {eachComponent.selected ? (
                    <Dropdown
                      width="200px"
                      defaultSelectedItem={
                        statusOptions
                          ? statusOptions.find(
                              status => status.id === eachComponent.status
                            ) || statusOptions[0]
                          : []
                      }
                      items={statusOptions || []}
                      itemToString={customRender}
                      itemComponent={customRender}
                      disabled={!eachComponent.selected}
                      onChange={newStatus => {
                        modifyComponentStatus(eachComponent, newStatus);
                      }}
                    />
                  ) : (
                    <StatusContainer>
                      {customRender(
                        items.find(item => item.id === eachComponent.status)
                      )}
                    </StatusContainer>
                  )}
                </Flex>
              );
            })}
          </Box>
        </TreeWrapper>
      )}
    </>
  );
};

const TreeWrapper = styled.div`
  box-sizing: border-box;
  margin-bottom: 16px;
  margin-top: 8px;
  width: 100%;

  .title-container {
    background: #fff;
    position: sticky;
    top: ${props => (props.isDrawer ? 0 : '64px')};
    z-index: 1;
    border-bottom: 0.5px solid #e8e8e8;
    padding: 12px 4px;
    height: 50px;
  }

  .dropdown-status {
    padding-top: 3px;
  }

  .checkbox ~ div {
    word-break: break-all;
    padding-right: 5px;
  }
`;

const BlankState = styled.div`
  margin: 100px 0;
  p {
    font-size: 14px;
    line-height: 25px;
    margin-top: 20px;
    text-align: center;
    color: #475867;
  }
`;

const IconHolder = styled.div`
  padding-left: 2px;
`;

const StyledSearchInput = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 1rem;
  .input-basic {
    border: 0 !important;
    border-radius: 0 !important;
    &:focus-within {
      box-shadow: none !important;
      border-bottom: 0 !important;
      border-radius: 0 !important;
    }
  }
`;

const CustomSelect = styled.div`
  display: flex;
  align-items: center;
  span:first-child {
    display: grid;
    place-content: center;
    margin-right: 8px;
  }
`;

const StatusContainer = styled.div`
  opacity: 0.6;
  min-height: 38px;
  border-radius: 5px;
  padding: 4px 12px;
  border: 1px solid #cfd7df;
  display: flex;
  width: 200px;
  max-width: 200px;
  align-items: center;
  display: flex;
  outline: none;
  border-radius: 4px;
  font-size: 14px;
  white-space: nowrap;
`;
export default ComponentsTree;
